/**
 * The person's unsaved edits, as the memo chat is told of them (DOP-654).
 *
 * The edit draft lives in this browser, so the chat's view of the memo is
 * the last saved version. A change proposed against that can miss: the
 * paragraph it names may already read differently on screen. With each
 * question the rail sends the parts of the memo the draft changes, as they
 * read now, and the values typed, so a proposal is written against what the
 * person sees.
 *
 * It is a description of the draft, not the draft: text only, no editor
 * state, and bounded, since it rides on every turn. The server stages it as
 * the person's own typing and nothing in it is a fact of the run.
 */

import type { JSONContent } from "@tiptap/react";

import {
	type StoredDraft,
	draftDoc,
	editSections,
	startMemoDoc,
} from "../../hooks/useMemoEditDraft";
import type { DealScreeningRun } from "../../types/dealScreening";
import { chartValues } from "./chartModel";
import { formatTableValue } from "./dealScreeningFormat";
import { sourceText } from "./documentSource";
import { columnName, figuresFromStored, figuresGrid, headerChanges, samePeriod } from "./figuresEditModel";
import { isEditableSection, itemsFromDoc, itemsKey, sectionChangesOf, splitMemoDoc, titlesOf } from "./memoEditorModel";
import { type EditItem, currentTable } from "./sectionEditModel";
import { FINANCIAL_COLUMNS, FINANCIAL_ROWS, orderSetting } from "./tableOrder";

export interface DraftParagraph {
	kind: "prose" | "bullets" | "table" | "chart";
	/** Whether it reads differently from the saved memo. */
	changed: boolean;
	lead?: string;
	text?: string;
	caption?: string;
	columns?: string[];
	rows?: string[][];
}

export interface DraftSection {
	key: string;
	title: string;
	paragraphs: DraftParagraph[];
	/** Paragraphs of the saved memo the draft takes out. */
	removed: string[];
}

export interface DraftValue {
	label: string;
	column: string | null;
	before: string;
	after: string;
	source: string;
}

export interface DraftSummary {
	sections: DraftSection[];
	values: DraftValue[];
	truncated: boolean;
}

/** What one turn may carry of a draft, in characters of text. */
export const DRAFT_BUDGET = 40_000;
const MAX_TEXT = 8_000;
const MAX_CELL = 500;
const MAX_ROWS = 60;
const MAX_COLUMNS = 12;
const MAX_PARAGRAPHS = 80;
const MAX_SECTIONS = 40;
const MAX_VALUES = 240;

const cut = (text: string, limit: number): string => (text.length > limit ? text.slice(0, limit) : text);
const clean = (text: string): string => text.replace(/[ \t]+/g, " ").trim();

const wording = (item: EditItem): string => [item.lead, item.text].filter(Boolean).join(" ");

/** The paragraph at the start that an item stands for, if it was there. */
const startOf = (item: EditItem, start: EditItem[]): EditItem | undefined =>
	item.anchor
		? start.find((s) => s.anchor === item.anchor)
		: item.table || item.chart
			? start.find((s) => !s.anchor && (s.table || s.chart) && itemsKey([s]) === itemsKey([item]))
			: start.find(
					(s) => !s.anchor && !s.table && !s.chart && (s.baseId ? s.baseId === item.baseId : wording(s) === wording(item)),
				);

function paragraphOf(item: EditItem, start: EditItem[]): DraftParagraph {
	const was = startOf(item, start);
	const changed = !was || itemsKey([was]) !== itemsKey([item]);
	if (item.chart) {
		// A chart goes as a table of its values (DOP-655).
		const values = chartValues(item.chart);
		return {
			kind: "chart",
			changed,
			...(item.chart.title ? { caption: cut(clean(item.chart.title), MAX_CELL) } : {}),
			columns: values.columns.slice(0, MAX_COLUMNS).map((c) => cut(clean(c), MAX_CELL)),
			rows: values.rows.slice(0, MAX_ROWS).map((row) => row.slice(0, MAX_COLUMNS).map((c) => cut(clean(c), MAX_CELL))),
		};
	}
	const table = item.table ? currentTable(item) : null;
	if (table) {
		return {
			kind: "table",
			changed,
			...(table.caption ? { caption: cut(clean(table.caption), MAX_CELL) } : {}),
			columns: table.columns.slice(0, MAX_COLUMNS).map((c) => cut(clean(c), MAX_CELL)),
			rows: table.rows
				.filter((row) => row.some((cell) => cell.trim()))
				.slice(0, MAX_ROWS)
				.map((row) => row.slice(0, MAX_COLUMNS).map((c) => cut(clean(c), MAX_CELL))),
		};
	}
	return {
		kind: item.kind,
		changed,
		...(item.lead.trim() ? { lead: cut(clean(item.lead), MAX_CELL) } : {}),
		// A list keeps its lines: one bullet is one line.
		text: cut(
			item.kind === "bullets" ? item.text.split("\n").map(clean).filter(Boolean).join("\n") : clean(item.text),
			MAX_TEXT,
		),
	};
}

const size = (section: DraftSection): number =>
	section.removed.reduce((n, text) => n + text.length, 0) +
	section.paragraphs.reduce(
		(n, p) =>
			n +
			(p.text?.length ?? 0) +
			(p.lead?.length ?? 0) +
			(p.rows ?? []).reduce((m, row) => m + row.reduce((k, cell) => k + cell.length, 0), 0),
		0,
	);

/**
 * What the draft changes, or null when there is no draft or it changes
 * nothing: a draft kept from a visit where nothing was typed is not news.
 */
export function draftSummary(
	run: DealScreeningRun,
	numberOf: (anchor: string | null | undefined) => number | null,
	stored: StoredDraft | null,
): DraftSummary | null {
	if (!stored) return null;
	const start = startMemoDoc(run, numberOf);
	const doc: JSONContent = draftDoc(stored, start) ?? start;
	const startParts = splitMemoDoc(start);
	const nowParts = splitMemoDoc(doc);
	const startTitles = titlesOf(start);
	const titles = titlesOf(doc);

	let truncated = false;
	let spent = 0;
	const sections: DraftSection[] = [];
	for (const section of editSections(run).filter(isEditableSection)) {
		const was = itemsFromDoc(section, startParts[section.key] ?? { type: "doc" });
		const now = itemsFromDoc(section, nowParts[section.key] ?? { type: "doc" });
		const title = (titles[section.key] ?? "").trim() || section.title;
		const renamed = title !== ((startTitles[section.key] ?? "").trim() || section.title);
		if (itemsKey(was) === itemsKey(now) && !renamed) continue;
		const found: DraftSection = {
			key: section.key,
			title: cut(title, 200),
			paragraphs: now.slice(0, MAX_PARAGRAPHS).map((item) => paragraphOf(item, was)),
			removed: was
				.filter((item) => (item.anchor ? !now.some((n) => n.anchor === item.anchor) : !startOf(item, now)))
				.map((item) =>
					cut(
						item.chart
							? `Chart: ${item.chart.title ?? ""}`
							: item.table
								? `Table: ${currentTable(item)?.caption ?? ""}`
								: clean(wording(item)),
						MAX_TEXT,
					),
				)
				.filter(Boolean)
				.slice(0, MAX_PARAGRAPHS),
		};
		if (now.length > MAX_PARAGRAPHS) truncated = true;
		// A section that does not fit is left out whole, and said to be: half
		// a section would read as the whole of it.
		if (sections.length >= MAX_SECTIONS || spent + size(found) > DRAFT_BUDGET) {
			truncated = true;
			continue;
		}
		spent += size(found);
		sections.push(found);
	}

	// Sections the person added in the draft (DOP-656): new to the memo, every
	// paragraph theirs. The saved sections of theirs they took out are named.
	const { added, removed } = sectionChangesOf(run, doc);
	for (const section of added) {
		const found: DraftSection = {
			key: section.key,
			title: cut(`${section.title} (new section)`, 200),
			paragraphs: section.items.slice(0, MAX_PARAGRAPHS).map((item) => paragraphOf(item, [])),
			removed: [],
		};
		if (sections.length >= MAX_SECTIONS || spent + size(found) > DRAFT_BUDGET) {
			truncated = true;
			continue;
		}
		spent += size(found);
		sections.push(found);
	}
	for (const key of removed) {
		const section = run.sections.find((s) => s.key === key);
		sections.push({ key, title: cut(`${section?.title ?? key} (section taken out)`, 200), paragraphs: [], removed: [] });
	}

	const figures = figuresFromStored(stored.figures);
	const grid = figuresGrid(run.facts, run.figures, figures, {
		columns: orderSetting(run.conventions, FINANCIAL_COLUMNS),
		rows: orderSetting(run.conventions, FINANCIAL_ROWS),
	});
	const rows = [...grid.series, ...grid.point];
	const typed: DraftValue[] = [
		...figures.entries.map((entry) => {
			const row = rows.find((r) => r.key === entry.key);
			const fact = row?.cells.find((cell) => samePeriod(cell.period, entry.period))?.fact;
			const unit = row?.unit ?? "usd_m";
			return {
				label: row?.label || entry.key,
				// The column as the person named it, not the draft's key for it.
				column: entry.period ? columnName(figures, entry.period) || "a new column" : null,
				before: fact && fact.value !== null ? formatTableValue(fact.value, unit) : "",
				after: formatTableValue(entry.value, unit),
				source: sourceText(entry.note, entry.source),
			};
		}),
		...headerChanges(run.facts, figures)
			.filter((change) => change.valid)
			.map((change) => ({
				label: change.label,
				column: null,
				before: change.before === "Not found" ? "" : change.before,
				after: change.after,
				source: change.note,
			})),
	];
	if (typed.length > MAX_VALUES) truncated = true;
	const values: DraftValue[] = typed.slice(0, MAX_VALUES).map((value) => ({
		label: cut(value.label, 200),
		column: value.column === null ? null : cut(value.column, 64),
		before: cut(value.before, MAX_CELL),
		after: cut(value.after, 2000),
		source: cut(value.source, 600),
	}));

	if (sections.length === 0 && values.length === 0) return null;
	return { sections, values, truncated };
}
